import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { AdminAuthProvider, useAdminAuth } from "@/lib/adminAuth";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyOtp from "@/pages/VerifyOtp";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import PatientDetail from "@/pages/PatientDetail";
import Consultation from "@/pages/Consultation";
import Profile from "@/pages/Profile";
import Availability from "@/pages/Availability";
import Facilities from "@/pages/Facilities";
import PatientJourney from "@/pages/PatientJourney";
import Appointments from "@/pages/Appointments";
import RxGroups from "@/pages/RxGroups";
import CareCircle from "@/pages/CareCircle";
import Referrals from "@/pages/Referrals";
import SecondOpinion from "@/pages/SecondOpinion";
import PublicPrescription from "@/pages/PublicPrescription";
import PublicBill from "@/pages/PublicBill";
import Activate from "@/pages/Activate";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminDoctors from "@/pages/admin/AdminDoctors";
import AdminPatients from "@/pages/admin/AdminPatients";
import AdminNudges from "@/pages/admin/AdminNudges";
import AdminPRD from "@/pages/admin/AdminPRD";

function Guarded({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#8A9992]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminGuarded({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#8A9992]">Loading…</div>;
  if (!admin) return <Navigate to="/back-office/login" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            {/* Public doctor pages */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-otp" element={<VerifyOtp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/activate/:token" element={<Activate />} />
            <Route path="/share/prescription/:token" element={<PublicPrescription />} />
            <Route path="/share/bill/:token" element={<PublicBill />} />

            {/* Doctor app (guarded) */}
            <Route path="/dashboard" element={<Guarded><Dashboard /></Guarded>} />
            <Route path="/patients/:id" element={<Guarded><PatientDetail /></Guarded>} />
            <Route path="/consultation/new" element={<Guarded><Consultation /></Guarded>} />
            <Route path="/profile" element={<Guarded><Profile /></Guarded>} />
            <Route path="/availability" element={<Guarded><Availability /></Guarded>} />
            <Route path="/facilities" element={<Guarded><Facilities /></Guarded>} />
            <Route path="/journey" element={<Guarded><PatientJourney /></Guarded>} />
            <Route path="/appointments" element={<Guarded><Appointments /></Guarded>} />
            <Route path="/rx-groups" element={<Guarded><RxGroups /></Guarded>} />
            <Route path="/care-circle" element={<Guarded><CareCircle /></Guarded>} />
            <Route path="/referrals" element={<Guarded><Referrals /></Guarded>} />
            <Route path="/second-opinion/:appointmentId" element={<Guarded><SecondOpinion /></Guarded>} />

            {/* Admin back office — separate token, separate provider */}
            <Route path="/back-office/login" element={<AdminLogin />} />
            <Route path="/back-office" element={<AdminGuarded><AdminLayout /></AdminGuarded>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminDoctors />} />
              <Route path="patients" element={<AdminPatients />} />
              <Route path="nudges" element={<AdminNudges />} />
              <Route path="prd" element={<AdminPRD />} />
            </Route>

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AdminAuthProvider>
    </AuthProvider>
  );
}
